import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  TextField,
  alpha,
} from '@mui/material';
import { Cancel as RejectIcon } from '@mui/icons-material';

const NEON = '#38BDF8';
const CORAL = '#F87171';
const SURFACE_BASE = '#050d1a';
const SURFACE_MID = '#0a1628';
const SURFACE_UP = '#0f1f38';
const B900 = '#1E3A8A';

const ConfirmRejectDialog = ({
  open = false,
  onClose,
  onConfirm,
  title = 'Reject claim',
  description = 'The claimant will see this reason. Rejected claims stay on record.',
  confirmLabel = 'Reject',
  cancelLabel = 'Cancel',
  loading = false,
  errorMessage = null,
  itemName = '',
  count = 1,
}) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  const trimmed = reason.trim();
  const canSubmit = trimmed.length > 0;

  const handleConfirm = () => {
    if (!canSubmit || loading) return;
    onConfirm(trimmed);
  };

  const label = count === 1 ? 'claim' : 'claims';

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '16px',
          background: `linear-gradient(160deg, ${alpha(SURFACE_BASE, 0.98)} 0%, ${alpha(SURFACE_MID, 0.96)} 100%)`,
          border: `1px solid ${alpha(NEON, 0.17)}`,
          boxShadow: [
            `0 0 0 1px ${alpha(B900, 0.9)}`,
            `0 24px 64px ${alpha('#000', 0.6)}`,
            `0 4px 16px ${alpha(CORAL, 0.08)}`,
            `inset 0 1px 0 ${alpha(NEON, 0.1)}`,
          ].join(', '),
          backdropFilter: 'blur(24px)',
          overflow: 'hidden',
        },
      }}
      BackdropProps={{
        sx: { background: 'rgba(3,9,18,0.72)', backdropFilter: 'blur(4px)' },
      }}
    >
      <DialogTitle sx={{
        pt: 2.5, pb: 1.5,
        background: `linear-gradient(135deg, ${alpha(CORAL, 0.04)}, transparent)`,
        borderBottom: `1px solid ${alpha(NEON, 0.1)}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
            background: `linear-gradient(135deg, ${alpha(CORAL, 0.22)}, ${alpha(CORAL, 0.5)})`,
            border: `1px solid ${alpha(CORAL, 0.32)}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: CORAL,
            boxShadow: `0 0 14px ${alpha(CORAL, 0.25)}`,
          }}>
            <RejectIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography sx={{
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: 700, fontSize: '1.1rem',
            color: CORAL, letterSpacing: 0.4,
          }}>
            {title}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5, pb: 1 }}>
        <Typography sx={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '0.9rem',
          color: alpha('#E2E8F0', 0.8),
          lineHeight: 1.65,
          mb: 2,
        }}>
          {count === 1 && itemName ? (
            <>
              Reject claim{' '}
              <Box component="span" sx={{ fontWeight: 700, color: alpha('#E2E8F0', 0.95) }}>
                &quot;{itemName}&quot;
              </Box>
              ?
            </>
          ) : (
            <>Reject {count} selected {label}?</>
          )}
        </Typography>
        <Typography sx={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '0.82rem',
          color: alpha(CORAL, 0.55),
          mb: 2,
          lineHeight: 1.55,
        }}>
          {description}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Rejection reason"
          placeholder="Explain why this claim is being rejected…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={loading}
          required
          sx={{
            '& .MuiInputLabel-root': { color: alpha('#94A3B8', 0.9) },
            '& .MuiOutlinedInput-root': {
              color: '#E2E8F0',
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '0.9rem',
              background: alpha(SURFACE_UP, 0.55),
              '& fieldset': { borderColor: alpha(NEON, 0.22) },
              '&:hover fieldset': { borderColor: alpha(CORAL, 0.45) },
              '&.Mui-focused fieldset': { borderColor: alpha(CORAL, 0.65) },
            },
          }}
        />
        {errorMessage ? (
          <Alert
            severity="error"
            sx={{
              mt: 2,
              borderRadius: '10px',
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '0.85rem',
              background: alpha('#450a0a', 0.92),
              border: '1px solid rgba(248,113,113,0.35)',
              color: '#fca5a5',
              '& .MuiAlert-icon': { color: '#f87171' },
            }}
          >
            {errorMessage}
          </Alert>
        ) : null}
      </DialogContent>

      <DialogActions sx={{
        px: 3, pb: 3, gap: 1.5,
        background: `linear-gradient(135deg, ${alpha(SURFACE_UP, 0.35)}, transparent)`,
        borderTop: `1px solid ${alpha(NEON, 0.09)}`,
      }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            borderRadius: '10px', px: 3,
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.875rem',
            color: alpha(NEON, 0.7),
            background: alpha(SURFACE_UP, 0.5),
            border: `1px solid ${alpha(NEON, 0.2)}`,
            '&:hover': { background: alpha(NEON, 0.07), borderColor: alpha(NEON, 0.45), color: NEON },
          }}
        >
          {cancelLabel}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading || !canSubmit}
          variant="contained"
          color="error"
          sx={{
            borderRadius: '10px',
            px: 4,
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 700,
            fontSize: '0.875rem',
            background: 'linear-gradient(180deg, #EF4444 0%, #DC2626 100%)',
            '&:hover': {
              background: 'linear-gradient(180deg, #F87171 0%, #B91C1C 100%)',
            },
          }}
        >
          {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmRejectDialog;
